/**
 * Registers the connector UI shipped with this DocReview build.
 *
 * The backend uses the same static-registration rule. Adding a provider is an
 * ordinary reviewed code change: define its label and optional feed actions in
 * this literal map. Runtime discovery and placeholder providers would obscure
 * what administrators can actually configure, so neither is supported.
 */
import { useEffect, type ComponentType } from 'react'
import { BriefcaseBusiness, CloudDownload, Database, Landmark, type LucideIcon } from 'lucide-react'

import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import { BoxFeedDialog } from './box/components/BoxFeedDialog'
import { useBoxConnection } from './box/hooks/useBoxConnection'
import { KualiResearchFeedDialog } from './kuali-research/components/KualiResearchFeedDialog'
import { ServiceNowFeedDialog } from './servicenow/components/ServiceNowFeedDialog'
import { connectorProviderLabel } from '../lib/provider-labels'
import type { ConnectorFeed, ConnectorProvider } from './types'
import { WorkdayFeedDialog } from './workday/components/WorkdayFeedDialog'

export interface ConnectorEditorProps {
  api: NodeApiClient
  orgId: string
  workspaceId: string
  vaultId: string
  feed?: ConnectorFeed
  feeds: ConnectorFeed[]
  userId?: string | null
  canEditQuery: boolean
  canAdministerWorkspace: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfigured: () => Promise<void>
  onSyncStarted: () => void
}

function BoxConnectorEditor({
  api,
  orgId,
  vaultId,
  feed,
  feeds,
  userId,
  canEditQuery,
  open,
  onOpenChange,
  onConfigured,
  onSyncStarted,
}: ConnectorEditorProps) {
  const box = useBoxConnection(api, orgId, vaultId, canEditQuery)
  const selectedFeed = feed ?? feeds.find(candidate => candidate.feedId === box.feedId)
  const effectiveFeed = selectedFeed?.provider === 'box' ? selectedFeed : undefined
  useEffect(() => {
    if (open && (!box.open || box.feedId !== effectiveFeed?.feedId)) {
      void box.openSource(effectiveFeed?.feedId)
    }
  }, [box.feedId, box.open, box.openSource, effectiveFeed?.feedId, open])
  useEffect(() => {
    if (box.open && !open) onOpenChange(true)
  }, [box.open, onOpenChange, open])
  if (!userId) return null
  return (
    <BoxFeedDialog
      api={api}
      orgId={orgId}
      vaultId={vaultId}
      feed={effectiveFeed}
      token={box.token}
      config={box.config}
      loadingConfig={box.loading}
      open={open && box.open}
      onOpenChange={(nextOpen) => {
        box.setOpen(nextOpen)
        onOpenChange(nextOpen)
      }}
      onConfigured={onConfigured}
      onSyncStarted={onSyncStarted}
      onConfigChanged={box.refreshConfig}
      onDisconnect={box.disconnect}
      onConnect={box.connect}
    />
  )
}

function KualiResearchConnectorEditor(props: ConnectorEditorProps) {
  return (
    <KualiResearchFeedDialog
      api={props.api}
      orgId={props.orgId}
      workspaceId={props.workspaceId}
      vaultId={props.vaultId}
      feed={props.feed?.provider === 'kuali-research' ? props.feed : undefined}
      canEditQuery={props.canEditQuery}
      canManageCredentials={props.canAdministerWorkspace}
      open={props.open}
      onOpenChange={props.onOpenChange}
      onConfigured={props.onConfigured}
    />
  )
}

function ServiceNowConnectorEditor(props: ConnectorEditorProps) {
  return (
    <ServiceNowFeedDialog
      api={props.api}
      orgId={props.orgId}
      workspaceId={props.workspaceId}
      vaultId={props.vaultId}
      feed={props.feed?.provider === 'servicenow' ? props.feed : undefined}
      canEditQuery={props.canEditQuery}
      canManageCredentials={props.canAdministerWorkspace}
      open={props.open}
      onOpenChange={props.onOpenChange}
      onConfigured={props.onConfigured}
      onSyncStarted={props.onSyncStarted}
    />
  )
}

function WorkdayConnectorEditor(props: ConnectorEditorProps) {
  return (
    <WorkdayFeedDialog
      api={props.api}
      orgId={props.orgId}
      workspaceId={props.workspaceId}
      vaultId={props.vaultId}
      feed={props.feed?.provider === 'workday' ? props.feed : undefined}
      canEditFilters={props.canEditQuery}
      canManageCredentials={props.canAdministerWorkspace}
      open={props.open}
      onOpenChange={props.onOpenChange}
      onConfigured={props.onConfigured}
    />
  )
}

interface ConnectorRegistration {
  sourceLabel: string
  sourceDescription: string
  SourceIcon: LucideIcon
  Editor: ComponentType<ConnectorEditorProps>
}

export interface ConnectorDefinition extends ConnectorRegistration {
  label: string
}

const CONNECTORS: Record<ConnectorProvider, ConnectorRegistration> = {
  box: {
    sourceLabel: 'Box folder',
    sourceDescription: 'Folders of files from a Box enterprise account',
    SourceIcon: CloudDownload,
    Editor: BoxConnectorEditor,
  },
  'kuali-research': {
    sourceLabel: 'Kuali Research',
    sourceDescription: 'Awards, subawards, negotiations, proposals',
    SourceIcon: Landmark,
    Editor: KualiResearchConnectorEditor,
  },
  servicenow: {
    sourceLabel: 'ServiceNow table',
    sourceDescription: 'Records from an approved ServiceNow table',
    SourceIcon: Database,
    Editor: ServiceNowConnectorEditor,
  },
  workday: {
    sourceLabel: 'Workday requisitions',
    sourceDescription: 'SRM assessment forms and requisition metadata',
    SourceIcon: BriefcaseBusiness,
    Editor: WorkdayConnectorEditor,
  },
}

export const connectorDefinitions = () => (
  (Object.keys(CONNECTORS) as ConnectorProvider[]).map(
    provider => [provider, connectorDefinition(provider)] as const,
  )
)

export function connectorDefinition(
  provider: ConnectorProvider,
): ConnectorDefinition {
  return {
    ...CONNECTORS[provider],
    label: connectorProviderLabel(provider),
  }
}
